'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { css } from '@lastbrand/web-ui-styled-system/css'
import classnames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ProductReviewGallerySliderDS } from '@src/components/blocks/product-review-gallery-slider-ds'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { fetchLazyWidgetsBatch } from '@src/components/pl-base-widget/utils/fetch-lazy-widgets'
import { useLayoutContext, usePLPageMeta } from '@src/components/pl-page-context'
import { PLProductReviewGallerySlider } from '@src/quince-components/src/atoms/reviews/productReviewGallerySlider/PLProductReviewGallerySlider'
import { NUMBER_VISIBLE_SLIDES } from '@src/quince-components/src/atoms/reviews/productReviewGallerySlider/productReviewGallerySlider.constants'
import type ProductReview from '@src/quince-site-common/src/domains/ProductReview'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useReviewSystemV3Experiment from '@src/quince-site-common/src/hooks/useReviewSystemV3Experiment'
// PL-only photo-popup state manager + popups. Used so the shared legacy popups/manager stay untouched.
import plReviewPhotoPopupStateManager from '@src/quince-site-common/src/redux/globalStateManager/PLReviewPhotoPopupStateManager'
// Reuse the legacy full-reviews section styles (summary padding/margins, gallery margins) so the PL
// FULL surface visually matches non-PL ProductReviews — same classes the organism applies.
import reviewSectionStyles from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/productReviews.module.scss'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { adaptReviewToGallery } from '../shared/reviews.adapter'
import { EMPTY_PRODUCT_PROPERTIES, GALLERY_PAGE_SIZE, GALLERY_PREFETCH_THRESHOLD } from '../shared/reviews.constants'
import { useReviewsProduct } from '../shared/reviews.context'

import type { Pagination } from '@lastbrand/storefront-proto-registry/api/v1/common'
import type { ReviewsGalleryWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const galleryWrapperCss = css.raw({
  marginTop: { base: '0', lg: 'space-100' },
  marginBottom: { base: 'space-40', lg: 'space-64' },
})
const galleryNoMarginCss = css.raw({ marginTop: '0', marginBottom: '0' })

// ---------------- REVIEWS_GALLERY (photo strip) ----------------
// APPEND + PREFETCH-AHEAD pagination. The gallery keeps a single growing list (initial page 0 from
// props + every appended page), so the user scrolls freely back and forth with position preserved.
// The next page is fetched when the user comes within GALLERY_PREFETCH_THRESHOLD images of the end
// (not AT the end) — so new content is appended before the edge is reached and the desktop next
// arrow never stalls in its disabled `isEnd` state. The same page-based source also feeds the photo
// popup: opening it hands over a `fetchMore` loader so the popup paginates the SAME stream (and
// growing the popup grows the gallery too).
function ReviewsGalleryWidgetComponent(props: PLTypedWidgetProps<ReviewsGalleryWidgetData>) {
  const data = props.data.reviewsGalleryData
  const pageMeta = usePLPageMeta()
  const layoutContext = useLayoutContext()
  const reviewSystemExp = useReviewSystemV3Experiment()
  const isReviewSystemV3Enabled = reviewSystemExp.showReviewSummaryAndFilterTags
  // Desktop shows NUMBER_VISIBLE_SLIDES (8) images at once; mobile ~1. The near-end check must offset
  // by the visible count — otherwise on desktop activeIndex tops out at (totalImages - visibleSlides)
  // and never reaches (totalImages - threshold), so the next page never loads (the desktop bug).
  const isDesktop = useDeviceInfo().deviceType === DEVICE_TYPES.DESKTOP

  // The first page arrives via the lazy widget fetch (props.data); subsequent pages accumulate as
  // the user swipes the gallery near its end. galleryReviews is the single growing list both the
  // slider and the photo popup read from.
  const initialReviews = useMemo<ProductReview[]>(() => (data?.items ?? []).map(adaptReviewToGallery), [data])
  const [appendedReviews, setAppendedReviews] = useState<ProductReview[]>([])
  const [paginationInfo, setPaginationInfo] = useState<Pagination | undefined>(props.pagination)
  const isFetchingRef = useRef(false)
  // Latch so we fetch only ONCE per approach to the end. afterChange fires on every scroll tick, so
  // without this a single swipe to the end fires N fetches; it resets when the user scrolls back
  // away from the end (so the next page can load when they reach the new end).
  const hasTriggeredNearEndRef = useRef(false)
  // 0-based index of the last page already loaded. The gallery is page-number based (the response
  // carries { pageNo, limit, total, hasMore } — no cursor), so each fetch must request the NEXT
  // pageNo; echoing the response pagination back would re-request the same page and append dupes.
  const loadedPageNoRef = useRef(props.pagination?.pageNo ?? 0)

  const galleryReviews = useMemo(() => [...initialReviews, ...appendedReviews], [initialReviews, appendedReviews])
  const hasMore = paginationInfo?.hasMore ?? false

  // Publish this product's spid (present on every review item) to the shared holder so the summary's
  // write-a-review button can resolve the product. The gallery sits at the top of the FULL surface
  // alongside the summary — publishing here (not only from the below-the-fold list) makes the spid
  // available before the user can click write-a-review. Set once; no-op when the container is absent.
  const reviewsProductCtx = useReviewsProduct()
  useEffect(() => {
    const spid = data?.items[0]?.productId
    if (spid) reviewsProductCtx?.publishSpid(String(spid))
  }, [data, reviewsProductCtx])

  // Each image is one slide, so the swiper's activeIndex is an image index across all reviews.
  const totalImages = useMemo(
    () => galleryReviews.reduce((count, review) => count + (review.imagesFileUrls?.length ?? 0), 0),
    [galleryReviews],
  )

  // Fetches the next page, appends it to the gallery's own list, and RETURNS the newly-mapped
  // reviews + the updated hasMore — so the popup feeder (appendToPopup) can mirror the same page
  // into the open photo popup without a second request.
  const fetchNext = useCallback(async (): Promise<{ reviews: ProductReview[]; hasMore: boolean }> => {
    if (isFetchingRef.current || !hasMore) return { reviews: [], hasMore }
    isFetchingRef.current = true
    const requestPageNo = loadedPageNoRef.current + 1
    const limit = paginationInfo?.limit ?? GALLERY_PAGE_SIZE
    try {
      const resolved = await fetchLazyWidgetsBatch(
        [
          {
            id: props.widgetId,
            type: WidgetType.REVIEWS_GALLERY,
            // Request the NEXT page explicitly (0-based pageNo), like the REVIEWS_LIST surface.
            request: { reviewsGallery: { pagination: { pageNo: requestPageNo, limit } } },
          },
        ],
        pageMeta,
        layoutContext,
      )
      const nextItems = resolved[0]?.data?.reviewsGalleryData?.items ?? []
      const nextPagination = resolved[0]?.pagination
      const mapped = nextItems.map(adaptReviewToGallery)
      if (mapped.length > 0) {
        setAppendedReviews(prev => [...prev, ...mapped])
        loadedPageNoRef.current = requestPageNo
      }
      // Prefer the response's hasMore; if it's absent, infer from a short final page, and stop when
      // nothing came back.
      const nextHasMore = nextPagination?.hasMore ?? mapped.length === limit
      setPaginationInfo(nextPagination ?? { ...(paginationInfo ?? {}), pageNo: requestPageNo, hasMore: nextHasMore })
      return { reviews: mapped, hasMore: nextHasMore }
    } catch (error) {
      // Halt pagination on failure so a transient error doesn't wedge the swiper into a retry loop.
      console.error('Failed to load more review gallery images:', error)
      setPaginationInfo(prev => (prev ? { ...prev, hasMore: false } : prev))
      return { reviews: [], hasMore: false }
    } finally {
      isFetchingRef.current = false
    }
  }, [hasMore, paginationInfo, pageMeta, layoutContext, props.widgetId])

  // The swiper binds its scroll listener ONCE on mount (useSwiperNavigation: addEventListener in a
  // []-effect), so it captures whatever `afterChange` exists on first render forever. To avoid that
  // stale closure freezing totalImages/hasMore at their page-0 values (which wedges the latch and
  // stops pagination while hasMore is still true), keep handleAfterChange STABLE and read the live
  // values through refs.
  const totalImagesRef = useRef(totalImages)
  totalImagesRef.current = totalImages
  const hasMoreRef = useRef(hasMore)
  hasMoreRef.current = hasMore
  const fetchNextRef = useRef(fetchNext)
  fetchNextRef.current = fetchNext
  const visibleSlidesRef = useRef(1)
  visibleSlidesRef.current = isDesktop ? NUMBER_VISIBLE_SLIDES : 1

  // Fires on every scroll (mobile swipe + desktop arrow). Prefetch the next page exactly once when
  // the RIGHTMOST VISIBLE slide first nears the last loaded image (offset by the visible count so
  // desktop, which can't scroll activeIndex to the very end, still triggers); reset the latch once
  // they move back from the end so the following page can load when they reach the new (grown) end.
  const handleAfterChange = useCallback(({ activeIndex }: { activeIndex: number }) => {
    const isNearEnd = activeIndex + visibleSlidesRef.current >= totalImagesRef.current - GALLERY_PREFETCH_THRESHOLD
    if (!isNearEnd) {
      hasTriggeredNearEndRef.current = false
      return
    }
    if (hasMoreRef.current && !hasTriggeredNearEndRef.current) {
      hasTriggeredNearEndRef.current = true
      void fetchNextRef.current()
    }
  }, [])

  // Next-page loader handed to the popup: loads the next gallery page (also growing the gallery's
  // own list via fetchNext) and mirrors it into the open popup. Stable identity — reads the live
  // fetchNext through the ref — so it survives being captured in the popup's payload.
  const appendToPopup = useCallback(async () => {
    const { reviews, hasMore: more } = await fetchNextRef.current()
    if (reviews.length > 0) plReviewPhotoPopupStateManager.appendReviews(reviews, more)
    else plReviewPhotoPopupStateManager.setHasMore(more)
  }, [])

  // Open the shared (globally-mounted) ReviewPopUp seeded with the accumulated gallery, plus the
  // hasMore gate + a loader so the popup paginates its own swiper (same page-based source as the
  // gallery). Non-PL callers omit these, so their popup keeps showing the full set with no fetch.
  const openReviewPopup = useCallback(
    (reviewId: number, imageIndex: number) => {
      plReviewPhotoPopupStateManager.showReviewPopUp({
        productReviews: galleryReviews,
        reviewId,
        imageIndex,
        hasMore: hasMoreRef.current,
        fetchMore: appendToPopup,
      })
    },
    [galleryReviews, appendToPopup],
  )

  if (galleryReviews.length === 0) return null

  if (shouldUseDesignSystem()) {
    return (
      <div className={css(galleryWrapperCss, isReviewSystemV3Enabled && galleryNoMarginCss)}>
        <ProductReviewGallerySliderDS
          heading={data?.title ?? ''}
          productReviews={galleryReviews}
          onReviewImageClick={openReviewPopup}
          afterChange={handleAfterChange}
          productProperties={EMPTY_PRODUCT_PROPERTIES}
          isReviewSystemV3Enabled={isReviewSystemV3Enabled}
        />
      </div>
    )
  }

  // Same gallery wrapper the non-PL organism uses (productReviews.module.scss) so the slider keeps
  // its vertical margins; V3 collapses them via noVerticalMargin to match non-PL spacing.
  return (
    <div
      className={classnames(reviewSectionStyles.productReviewsImageGallery, {
        [reviewSectionStyles.noVerticalMargin ?? '']: isReviewSystemV3Enabled,
      })}
    >
      <PLProductReviewGallerySlider
        heading={(data?.title ?? '').toUpperCase()}
        productReviews={galleryReviews}
        onReviewImageClick={openReviewPopup}
        afterChange={handleAfterChange}
        productProperties={EMPTY_PRODUCT_PROPERTIES}
        isReviewSystemV3Enabled={isReviewSystemV3Enabled}
      />
    </div>
  )
}

export const ReviewsGalleryWidget: PLTypedWidgetInterface<ReviewsGalleryWidgetData, WidgetType.REVIEWS_GALLERY> = {
  id: WidgetType.REVIEWS_GALLERY,
  errorBoundary: PLBaseErrorBoundary,
  render: ReviewsGalleryWidgetComponent,
}
